// screens/SimulaPostMortemScreen.tsx — W3 Phase C read-only Simula post-mortem,
// V28-restyled (S4 surface, simula-result.html + simula-review.html).
//
// Mounted at /post-mortem/simula/:sessionId. Renders the historical verdict +
// per-question replay for a finalized Simula session. We deliberately do NOT
// URL-param-hydrate the live SimulaScreen state machine — its phase machine
// assumes an in-progress simulation, and mixing read-only history into it is a
// regression risk. This screen is its own simpler shape.
//
// V28 contract (build-guard S4 + simula-result/simula-review prototypes):
//   - 3-outcome verdict hero (PERFETTO 30/30 / PROMOSSO / "Ci sei quasi") with
//     sage (passed) / rose (not passed) styling, icon + color (never color
//     alone). "Ci sei quasi" surfaces a Progressi CTA per spec §5.5.
//   - "Dove hai sbagliato" breakdown: clickable rows (grouped by the only
//     grouping key the read-only payload carries — principle id) that JUMP
//     (scroll) the student to that error's review card below. Reduced-motion
//     gated.
//   - Per-error review thread: each reviewed wrong answer as a card using the
//     <VeroFalsoPills> sage/rose verdict vocabulary — "La tua risposta" (rose)
//     vs "Risposta corretta" (sage), icon + color.
//   - Hamburger LEFT (drawer), NO back arrow — gesture-back via AppFrame.
//
// DATA HONESTY (build-guard S4 + task): every number/outcome is real session
// state from getSimulaSummary. The read-only post-mortem payload exposes
// correctCount / totalQuestions / passed / questions[]{principleId, studentAnswer,
// correctAnswer} — but it does NOT carry the exam probability nor per-argomento
// names nor question text (those exist only in the LIVE finalize() response
// SimulaScreen consumes). We therefore do NOT render a probability nudge here
// and do NOT invent argomento names or claim text — surfacing a fabricated
// number on a historical screen would be a trust violation. The probability
// band lives on the live result; this read-only viewer states only what it has.
//
// Italian "tu" voice. Light-only (tokens.mode always resolves 'light'). 44px taps.

import type { JSX, MutableRefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useShellNav } from '../components/AppFrame';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';
import { resetForDrawerTarget } from '../state/drawer-nav';
import { sessionsApi, type SimulaSummaryPayload, type SimulaSummaryQuestion, type SimulaPostMortemMode } from '../api/sessions';
import { ApiError } from '../api/client';
import { TAP_TARGET_MIN } from '../tokens';

type LoadPhase =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'error' }
  | { kind: 'ready'; data: SimulaSummaryPayload };

const MODE_LABEL: Record<SimulaPostMortemMode, string> = {
  simula_cieca:     'cieca',
  simula_guidata:   'guidata',
  simula_difficile: 'difficile',
};

// Patente B blind-exam pass threshold: ≤3 errors over 30 (≥27/30). Used only
// for the honest "sotto/oltre il limite di 3" subline; the authoritative
// pass/fail is `simulation.passed` from the finalize RPC.
const ERROR_LIMIT = 3;

function formatItalianDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const months = [
    'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
    'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
  ];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function vfLabel(v: boolean | null): string {
  if (v === null) return '—';
  return v ? 'Vero' : 'Falso';
}

/** A reviewed question paired with its 1-based display index in the exam. */
interface ReviewItem {
  index:    number;            // 1-based question position
  question: SimulaSummaryQuestion;
}

// Sage/rose resting borders match the VeroFalsoPills prototype rgba(...,0.45).
const SAGE_BORDER_45 = 'rgba(46, 141, 111, 0.45)';
const ROSE_BORDER_45 = 'rgba(197, 54, 91, 0.45)';

function CheckGlyph({ color }: { color: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" width={18} height={18} aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CrossGlyph({ color }: { color: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" width={18} height={18} aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronGlyph({ color }: { color: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" width={18} height={18} aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function SimulaPostMortemScreen(): JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const t = useTokens();
  const reduceMotion = useReducedMotion();
  const { goBack } = useShellNav();
  const [phase, setPhase] = useState<LoadPhase>({ kind: 'loading' });

  // Anchors for the clickable breakdown rows → scroll-jump to a review card.
  const reviewRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());

  useEffect(() => {
    if (!sessionId) {
      setPhase({ kind: 'not_found' });
      return;
    }
    let alive = true;
    setPhase({ kind: 'loading' });
    sessionsApi.getSimulaSummary(sessionId)
      .then((data) => { if (alive) setPhase({ kind: 'ready', data }); })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) {
          setPhase({ kind: 'not_found' });
        } else {
          setPhase({ kind: 'error' });
        }
      });
    return () => { alive = false; };
  }, [sessionId]);

  const jumpToReview = (index: number) => {
    const el = reviewRefs.current.get(index);
    if (!el) return;
    el.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block:    'center',
    });
  };

  // ── Subviews ──────────────────────────────────────────────────────────────

  const stateCard = (testid: string, role: 'alert' | 'status', heading: string, body: string) => (
    <div
      data-testid={testid}
      role={role}
      style={{
        padding:      '20px 16px',
        background:   t.surface,
        border:       `1px solid ${t.line}`,
        borderRadius: t.radius.lg,
        fontFamily:   t.fonts.body,
        fontSize:     14,
        color:        t.text,
        lineHeight:   1.5,
      }}
    >
      <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{heading}</p>
      <p style={{ margin: 0, color: t.textMuted }}>{body}</p>
    </div>
  );

  return (
    <Screen>
      <TopChrome
        title="Simula Esame"
        subtitle="Esito della simulazione"
        mode="simula"
        onBack={goBack}
      />

      <div
        style={{
          flex:          1,
          overflowY:     'auto',
          padding:       '16px 16px 32px',
          display:       'flex',
          flexDirection: 'column',
          gap:           16,
        }}
      >
        {phase.kind === 'loading' && (
          <div
            data-testid="simula-postmortem-loading"
            style={{
              padding:    '24px 12px',
              textAlign:  'center',
              color:      t.textMuted,
              fontFamily: t.fonts.body,
              fontSize:   14,
            }}
          >
            Sto caricando il tuo riepilogo…
          </div>
        )}

        {phase.kind === 'not_found' &&
          stateCard(
            'simula-postmortem-not-found',
            'alert',
            'Non riesco a trovare questa simulazione.',
            'È stata creata da un altro account o è stata eliminata.',
          )}

        {phase.kind === 'error' &&
          stateCard(
            'simula-postmortem-error',
            'alert',
            'Qualcosa non ha funzionato.',
            'Non sono riuscito a caricare il riepilogo. Riprova tra poco.',
          )}

        {phase.kind === 'ready' && (() => {
          const { data } = phase;
          const modeLabel = MODE_LABEL[data.mode];
          const dateStr = formatItalianDate(data.startedAt);

          // Mid-session abort: simulation is null. No verdict hero — render a
          // polite explanation. Live SimulaScreen owns the resume flow; the
          // post-mortem is a viewer-only surface.
          if (!data.simulation) {
            return (
              <SimulaAbortView modeLabel={modeLabel} dateStr={dateStr} />
            );
          }

          return (
            <SimulaResultView
              data={data}
              modeLabel={modeLabel}
              dateStr={dateStr}
              reviewRefs={reviewRefs}
              onJumpToReview={jumpToReview}
              onGoProgressi={() => { resetForDrawerTarget('progresso'); navigate('/progresso'); }}
            />
          );
        })()}
      </div>
    </Screen>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Abort view — no simulation_results row.
// ─────────────────────────────────────────────────────────────────────────────

function SimulaAbortView({ modeLabel, dateStr }: { modeLabel: string; dateStr: string }): JSX.Element {
  const t = useTokens();
  return (
    <>
      <h1
        data-testid="simula-postmortem-header"
        style={{
          fontFamily:    t.fonts.heading,
          fontSize:      20,
          fontWeight:    700,
          color:         t.text,
          letterSpacing: -0.3,
          margin:        0,
        }}
      >
        Simulazione {modeLabel} del {dateStr}
      </h1>
      <div
        data-testid="simula-postmortem-aborted"
        role="status"
        style={{
          padding:      '20px 16px',
          background:   t.surface,
          border:       `1px solid ${t.line}`,
          borderRadius: t.radius.lg,
          fontFamily:   t.fonts.body,
          fontSize:     14,
          color:        t.text,
          lineHeight:   1.5,
        }}
      >
        <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
          Hai interrotto questa simulazione prima della fine.
        </p>
        <p style={{ margin: 0, color: t.textMuted }}>
          Le risposte non sono state salvate per il riepilogo.
        </p>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Result view — verdict hero + breakdown + per-error review thread.
// ─────────────────────────────────────────────────────────────────────────────

interface SimulaResultViewProps {
  data:           SimulaSummaryPayload;
  modeLabel:      string;
  dateStr:        string;
  reviewRefs:     MutableRefObject<Map<number, HTMLDivElement | null>>;
  onJumpToReview: (index: number) => void;
  onGoProgressi:  () => void;
}

function SimulaResultView({
  data, modeLabel, dateStr, reviewRefs, onJumpToReview, onGoProgressi,
}: SimulaResultViewProps): JSX.Element {
  const t = useTokens();

  const sim = data.simulation!;
  const passed = sim.passed;
  const total = sim.totalQuestions;
  const correct = sim.correctCount;
  const errors = Math.max(0, total - correct);
  // P6 polish #1 / V28: three distinct outcomes (spec §5.5) — Perfetto (all
  // correct) / Promosso (passed) / "Ci sei quasi" (not passed → Progressi).
  // Every value here is real grading state; nothing is computed beyond the
  // finalize result the RPC already produced.
  const perfetto = total > 0 && correct === total;
  // FP10-M4 — honest LOW tier (kept in lockstep with the live SimulaScreen
  // hero). When the score is far below the soglia (correct < 20, i.e. >10
  // errori) "Ci sei quasi" is condescending and false, so the headline becomes
  // warm-but-honest Il Prof copy. Deliberate extension of the V28 3-outcome
  // contract from live evidence (founder can veto). The Progressi CTA below
  // (gated on !passed) already covers this tier — same CTA, honest headline.
  const LOW_SCORE_CEILING = 20; // correct < 20 (errori > 10)
  const lowScore = !passed && total > 0 && correct < LOW_SCORE_CEILING;
  const outcomeTitle = perfetto
    ? 'PERFETTO'
    : passed
      ? 'PROMOSSO'
      : lowScore
        ? 'Da costruire'
        : 'Ci sei quasi';
  const sage = passed; // sage on pass (incl. perfetto), rose on a non-pass
  const accent = sage ? t.right : t.wrong;
  const accentText = sage ? t.rightText : t.wrongText;
  const heroBg = sage ? t.rightSoft : t.wrongSoft;
  const heroBorder = sage ? SAGE_BORDER_45 : ROSE_BORDER_45;
  const limitLine = errors <= ERROR_LIMIT
    ? `${errors} ${errors === 1 ? 'errore' : 'errori'} su ${total} · sotto il limite di ${ERROR_LIMIT}`
    : lowScore
      ? `${errors} errori su ${total} · partiamo dalle basi, un passo alla volta`
      : `${errors} errori su ${total} · oltre il limite di ${ERROR_LIMIT}`;

  // The reviewable questions are those with a known wrong answer (the V28
  // "Dove hai sbagliato" thread). studentAnswer/correctAnswer come straight
  // from the recorded session events.
  const reviewItems: ReviewItem[] = useMemo(
    () =>
      data.questions
        .map((q, i) => ({ index: i + 1, question: q }))
        .filter(({ question }) =>
          question.studentAnswer !== null
          && question.correctAnswer !== null
          && question.studentAnswer !== question.correctAnswer,
        ),
    [data.questions],
  );

  // Breakdown grouped by the only grouping key the read-only payload carries:
  // the principle id. Argomento NAMES are not in this payload (they live on the
  // live finalize breakdown), so we surface the principle id honestly rather
  // than fabricate a friendly name.
  const breakdown = useMemo(() => {
    const byPrinciple = new Map<string, { key: string; errors: number; firstIndex: number }>();
    for (const { index, question } of reviewItems) {
      const key = question.principleId ?? 'sconosciuto';
      const existing = byPrinciple.get(key);
      if (existing) {
        existing.errors += 1;
      } else {
        byPrinciple.set(key, { key, errors: 1, firstIndex: index });
      }
    }
    return [...byPrinciple.values()].sort((a, b) => a.firstIndex - b.firstIndex);
  }, [reviewItems]);

  const completedDate = formatItalianDate(sim.completedAt);

  return (
    <>
      {/* ── Verdict hero (sage / rose, icon + color) ─────────────────────── */}
      <div
        data-testid="simula-postmortem-summary"
        style={{
          background:    heroBg,
          border:        `1px solid ${heroBorder}`,
          borderRadius:  t.radius.xl,
          padding:       '24px 20px',
          display:       'flex',
          flexDirection: 'column',
          alignItems:    'center',
          gap:           8,
          textAlign:     'center',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width:          56,
            height:         56,
            borderRadius:   18,
            background:     accent,
            display:        'inline-flex',
            alignItems:     'center',
            justifyContent: 'center',
            boxShadow:      `0 3px 10px ${sage ? 'rgba(46,141,111,0.3)' : 'rgba(197,54,91,0.3)'}`,
          }}
        >
          {sage
            ? <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" width={28} height={28}><polyline points="20 6 9 17 4 12" /></svg>
            : <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" width={28} height={28}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>}
        </span>
        <div
          data-testid="simula-postmortem-outcome"
          style={{
            fontFamily:    t.fonts.heading,
            fontSize:      perfetto || passed ? 30 : 26,
            fontWeight:    800,
            color:         accentText,
            letterSpacing: -0.5,
            lineHeight:    1.1,
          }}
        >
          {outcomeTitle}
        </div>
        <p style={{ margin: 0, color: accentText, fontWeight: 600, fontSize: 14 }}>
          {limitLine}
        </p>
        <div
          style={{
            display:    'flex',
            justifyContent: 'center',
            gap:        8,
            color:      t.textMuted,
            fontSize:   12.5,
            fontWeight: 600,
          }}
        >
          <span>{correct}/{total} corrette</span>
          <span style={{ color: t.textFaint }}>·</span>
          <span>{modeLabel} · {dateStr}</span>
        </div>
        <div style={{ fontFamily: t.fonts.body, fontSize: 12, color: t.textMuted }}>
          Completata il {completedDate}.
        </div>

        {/* "Ci sei quasi" routes the student to work the weak topics (§5.5). */}
        {!passed && (
          <button
            onClick={onGoProgressi}
            style={{
              marginTop:    8,
              minHeight:    TAP_TARGET_MIN,
              background:   t.wrong,
              color:        '#fff',
              border:       'none',
              borderRadius: t.radius.pill,
              padding:      '10px 20px',
              fontFamily:   t.fonts.body,
              fontSize:     14,
              fontWeight:   700,
              cursor:       'pointer',
            }}
          >
            Lavoriamo sugli argomenti deboli →
          </button>
        )}
      </div>

      {/* ── "Dove hai sbagliato" breakdown — clickable rows jump the review ── */}
      {breakdown.length > 0 && (
        <div
          data-testid="simula-postmortem-breakdown"
          style={{
            background:   t.surface,
            border:       `1px solid ${t.line}`,
            borderRadius: t.radius.lg,
            padding:      '14px 16px',
            display:      'flex',
            flexDirection:'column',
            gap:          4,
          }}
        >
          <div
            style={{
              fontFamily:    t.fonts.body,
              fontSize:      11,
              fontWeight:    700,
              color:         t.textFaint,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              marginBottom:  6,
            }}
          >
            Dove hai sbagliato
          </div>
          {breakdown.map((row) => (
            <button
              key={row.key}
              data-testid={`simula-postmortem-breakdown-row-${row.key}`}
              onClick={() => onJumpToReview(row.firstIndex)}
              aria-label={`Rivedi l'errore in ${row.key} (${row.errors} ${row.errors === 1 ? 'errore' : 'errori'})`}
              style={{
                width:          '100%',
                display:        'flex',
                alignItems:     'center',
                gap:            10,
                minHeight:      TAP_TARGET_MIN,
                padding:        '8px 0',
                borderBottom:   `1px solid ${t.line}`,
                background:     'transparent',
                border:         'none',
                textAlign:      'left',
                cursor:         'pointer',
                fontFamily:     t.fonts.body,
                fontSize:       13.5,
                color:          t.text,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  minWidth:       28,
                  height:         24,
                  padding:        '0 6px',
                  borderRadius:   8,
                  background:     t.wrongSoft,
                  color:          t.wrongText,
                  display:        'inline-flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  fontWeight:     700,
                  fontSize:       12,
                }}
              >
                {row.firstIndex}
              </span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.key}
              </span>
              <span style={{ color: t.textMuted, fontWeight: 700, fontSize: 12.5, flexShrink: 0 }}>
                {row.errors} {row.errors === 1 ? 'errore' : 'errori'}
              </span>
              <ChevronGlyph color={t.accent} />
            </button>
          ))}
        </div>
      )}

      {/* ── Per-error review thread ──────────────────────────────────────── */}
      {data.questions.length === 0 ? (
        <div
          data-testid="simula-postmortem-empty"
          style={{
            padding:      '20px 16px',
            background:   t.surface,
            border:       `1px solid ${t.line}`,
            borderRadius: t.radius.lg,
            fontFamily:   t.fonts.body,
            fontSize:     14,
            color:        t.textMuted,
            textAlign:    'center',
          }}
        >
          Nessun dettaglio per le risposte di questa simulazione.
        </div>
      ) : reviewItems.length === 0 ? (
        <div
          data-testid="simula-postmortem-no-errors"
          style={{
            padding:      '18px 16px',
            background:   t.rightSoft,
            border:       `1px solid ${SAGE_BORDER_45}`,
            borderRadius: t.radius.lg,
            fontFamily:   t.fonts.body,
            fontSize:     14,
            color:        t.rightText,
            textAlign:    'center',
            fontWeight:   600,
          }}
        >
          Nessun errore da rivedere: tutte le risposte registrate sono corrette.
        </div>
      ) : (
        <ul
          data-testid="simula-postmortem-questions"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {reviewItems.map(({ index, question }) => (
            <li
              key={`${question.questionId ?? index}-${index}`}
              data-testid={`simula-postmortem-question-${index - 1}`}
            >
              <ReviewErrorCard
                index={index}
                question={question}
                anchorRef={(el) => reviewRefs.current.set(index, el)}
              />
            </li>
          ))}
        </ul>
      )}

      {data.truncated && (
        <div
          data-testid="simula-postmortem-truncated"
          style={{
            fontFamily: t.fonts.body,
            fontSize:   10,
            color:      t.textFaint,
            textAlign:  'center',
            marginTop:  4,
          }}
        >
          Hai più di 200 risposte: ti mostro le prime 200.
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// One reviewed error — claim-less (the read-only payload carries no question
// text), so we render the VeroFalsoPills sage/rose verdict vocabulary: "La tua
// risposta" (rose, wrong) vs "Risposta corretta" (sage). Icon + color always.
// ─────────────────────────────────────────────────────────────────────────────

function ReviewErrorCard({
  index, question, anchorRef,
}: {
  index:     number;
  question:  SimulaSummaryQuestion;
  anchorRef: (el: HTMLDivElement | null) => void;
}): JSX.Element {
  const t = useTokens();
  const studentSaidVero = question.studentAnswer === true;
  const correctIsVero = question.correctAnswer === true;

  return (
    <div ref={anchorRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          fontFamily:    t.fonts.body,
          fontSize:      11,
          fontWeight:    700,
          color:         t.textFaint,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        Errore · Domanda {index}
        {question.principleId && (
          <span style={{ marginLeft: 8, fontWeight: 600, color: t.textFaint, letterSpacing: 0 }}>
            · {question.principleId}
          </span>
        )}
      </div>
      <div
        style={{
          background:    t.surface,
          border:        `1px solid ${t.line}`,
          borderRadius:  16,
          boxShadow:     '0 1px 2px rgba(26,21,53,0.04), 0 6px 18px rgba(26,21,53,0.05)',
          padding:       16,
          display:       'flex',
          flexDirection: 'column',
          gap:           12,
        }}
      >
        {/* La tua risposta — wrong (rose) */}
        <VerdictRow
          tone="rose"
          choiceIsVero={studentSaidVero}
          choiceLabel={vfLabel(question.studentAnswer)}
          heading="La tua risposta"
          sub={`Hai risposto ${vfLabel(question.studentAnswer)} — non corretta`}
        />
        {/* Risposta corretta — sage */}
        <VerdictRow
          tone="sage"
          choiceIsVero={correctIsVero}
          choiceLabel={vfLabel(question.correctAnswer)}
          heading="Risposta corretta"
          sub={`L'affermazione è ${correctIsVero ? 'vera' : 'falsa'}`}
        />
      </div>
    </div>
  );
}

function VerdictRow({
  tone, choiceIsVero, choiceLabel, heading, sub,
}: {
  tone:         'sage' | 'rose';
  choiceIsVero: boolean;
  choiceLabel:  string;
  heading:      string;
  sub:          string;
}): JSX.Element {
  const t = useTokens();
  // The chip color follows the V/F CHOICE (Vero = sage check, Falso = rose
  // cross), matching VeroFalsoPills. `tone` only tints the row container.
  const chipAccent = choiceIsVero ? t.right : t.wrong;
  const chipText = choiceIsVero ? t.rightText : t.wrongText;
  const chipBg = choiceIsVero ? t.rightSoft : t.wrongSoft;
  const rowBg = tone === 'sage' ? t.rightSoft : t.wrongSoft;
  const rowBorder = tone === 'sage' ? SAGE_BORDER_45 : ROSE_BORDER_45;

  return (
    <div
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        padding:      '10px 12px',
        background:   rowBg,
        border:       `1px solid ${rowBorder}`,
        borderRadius: t.radius.md,
      }}
    >
      <span
        style={{
          display:      'inline-flex',
          alignItems:   'center',
          gap:          6,
          padding:      '4px 10px',
          minHeight:    28,
          borderRadius: t.radius.pill,
          background:   chipBg,
          border:       `1px solid ${choiceIsVero ? SAGE_BORDER_45 : ROSE_BORDER_45}`,
          color:        chipText,
          fontFamily:   t.fonts.body,
          fontWeight:   700,
          fontSize:     13,
          flexShrink:   0,
        }}
      >
        {choiceIsVero ? <CheckGlyph color={chipAccent} /> : <CrossGlyph color={chipAccent} />}
        {choiceLabel}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: t.fonts.body, fontSize: 13.5, fontWeight: 700, color: t.text }}>
          {heading}
        </div>
        <div style={{ fontFamily: t.fonts.body, fontSize: 12, color: t.textMuted, marginTop: 2 }}>
          {sub}
        </div>
      </div>
    </div>
  );
}
